import { Component } from '@theme/component';

/**
 * @typedef {object} Refs
 * @property {HTMLButtonElement[]} tabs
 * @property {HTMLElement[]} panels
 * @property {HTMLElement[]} soundToggles
 * @property {HTMLElement} tabsRow
 *
 * @extends {Component<Refs>}
 */
export class PlayerGuideComponent extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.#relocateAppBlocks();
    this.#relocateTabs();

    const tabParam = new URLSearchParams(window.location.search).get('tab');
    if (tabParam) {
      const tab = (this.refs.tabs ?? []).find((t) => t.dataset.tab === tabParam);
      if (tab) this.#activate(tab);
    }
  }

  /** @param {PointerEvent} event */
  selectPanel(event) {
    const tab = /** @type {HTMLButtonElement} */ (event.target);
    this.#activate(tab);

    if (tab.dataset.tab) {
      const url = new URL(window.location.href);
      url.searchParams.set('tab', tab.dataset.tab);
      history.replaceState(null, '', url);
    }

    tab.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
  }

  /**
   * Tapping a video's sound icon mutes every other player video on the page and
   * unmutes this one; tapping the same one again mutes it back.
   *
   * @param {PointerEvent} event
   */
  toggleSound(event) {
    const wrapper = /** @type {HTMLElement} */ (event.target);
    const video = wrapper.querySelector('video');
    if (!video) return;

    const shouldUnmute = video.muted;

    for (const toggle of this.refs.soundToggles ?? []) {
      const otherVideo = toggle.querySelector('video');
      if (otherVideo) otherVideo.muted = true;
      toggle.classList.remove('is-unmuted');
    }

    if (shouldUnmute) {
      video.muted = false;
      wrapper.classList.add('is-unmuted');
    }
  }

  /**
   * Tapping the center play/pause button toggles that video only.
   *
   * @param {PointerEvent} event
   */
  togglePlay(event) {
    const button = /** @type {HTMLElement} */ (event.target);
    const wrapper = button.closest('.player-media--video');
    const video = wrapper?.querySelector('video');
    if (!video) return;

    if (video.paused) {
      video.play();
    } else {
      video.pause();
    }

    wrapper.classList.toggle('is-paused', video.paused);
  }

  /**
   * Any block in the tabs grid that isn't one of our own product tabs (e.g. an
   * app block added via the editor's Apps picker) gets moved into the reviews
   * card slot instead. Skipped in the theme editor -- Shopify.designMode is
   * true there -- since moving a block's rendered DOM breaks the editor's
   * click-to-select overlay, which maps click zones to blocks' original
   * render position.
   */
  #relocateAppBlocks() {
    if (window.Shopify?.designMode) return;

    const grid = this.querySelector('.players-page__grid');
    const slot = document.querySelector('[data-player-reviews-slot]');
    if (!grid || !slot) return;

    for (const child of [...grid.children]) {
      if (!child.classList.contains('player-tab')) {
        slot.append(child);
      }
    }
  }

  /**
   * Pills each used to have their own position:sticky, which meant a second
   * wrapped row stuck to the same offset as the first and covered it instead
   * of stacking below it. Moving all pills into one dedicated sticky wrapper
   * fixes that, since the wrapper (with its own internal wrap) sticks as a
   * single cohesive block. Skipped in the theme editor for the same
   * click-to-select reason as #relocateAppBlocks.
   */
  #relocateTabs() {
    if (window.Shopify?.designMode) return;

    const tabsRow = this.refs.tabsRow;
    if (!tabsRow) return;

    for (const tab of this.refs.tabs ?? []) {
      tabsRow.append(tab);
    }
  }

  /** @param {HTMLButtonElement} tab */
  #activate(tab) {
    const targetId = tab.dataset.target;
    if (!targetId) return;

    for (const t of this.refs.tabs ?? []) {
      t.setAttribute('aria-selected', `${t === tab}`);
    }

    for (const panel of this.refs.panels ?? []) {
      panel.hidden = panel.id !== targetId;
    }
  }
}

if (!customElements.get('player-guide-component')) {
  customElements.define('player-guide-component', PlayerGuideComponent);
}
